import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
	debounce as _debounce,
	has as _has,
	isArray as _isArray,
	isEmpty as _isEmpty,
	isNumber as _isNumber,
	isString as _isString,
	isUndefined as _isUndefined,
	omitBy as _omitBy,
	first as _first
} from 'lodash';
import moment from 'moment';
import { Col, Form, FormInstance, Row } from 'antd';
import { FILTER_CONDITIONS } from '@bringg/types';
import './filter-bar-2.scss';
import { FilterBase, FilterMore, FilterTag } from './components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { DefaultDateFormat } from '../../types/common.consts';
import {
	FilterBar2ConditionValue,
	FilterBar2FilterType,
	FilterBar2ListItem,
	FilterBar2Value,
	FilterBar2Values,
	RangeValue
} from './types';
import { BringgInput, Button } from '../index';
import { isMomentRange } from './components/internal/type-check/is-moment-range';
import { isPairOfDateRangeKeywords } from './components/date-range-v2';

export interface Translations {
	reset: string;
	moreFilters: string;
	done: string;
	cancel: string;
	all: string;
	selectAll: string;
	search: string;
	specifiedPeriod: string;
	preset: string;
	teams: string;
	subTeams: string;
	conditionFilter?: {
		operatorSelectPlaceholder: string;
		from?: string;
		to?: string;
		options: Partial<Record<FILTER_CONDITIONS, string>>;
	};
}

export interface FilterBar2Props {
	list: FilterBar2ListItem[];
	initialValues?: FilterBar2Values;
	onFilter: (update: { [key: string]: any }) => void;
	onReset?: (field?: string) => void;
	resetToInitial?: boolean;
	initialAdditional?: string[];
	onAdditional?: (additionals: string[]) => void;
	children?: Function;
	className?: string;
	translations: Translations;
	temporaryOpened?: boolean;
	dateFormat?: string;
	form?: FormInstance;
	applyFilterImmediately?: boolean;
	removeMissingFieldsOnInitialValuesChange?: boolean;
}

const FilterBar2 = ({
	children,
	className,
	list,
	onFilter,
	resetToInitial,
	initialValues = {},
	initialAdditional,
	onAdditional,
	onReset,
	translations,
	form: topForm,
	dateFormat = DefaultDateFormat,
	applyFilterImmediately = false,
	removeMissingFieldsOnInitialValuesChange = false
}: FilterBar2Props) => {
	const [form] = Form.useForm(topForm);
	const popoverContainerRef = useRef<HTMLDivElement>(null);

	const getValues = (): FilterBar2Values => _omitBy(form.getFieldsValue(true), _isUndefined);

	const isValuePresent = value => {
		// _.isEmpty for primitive values is always true, so only null and undefined will be treated as empty
		switch (typeof value) {
			case 'object':
				return !_isEmpty(value);
			case 'number':
				return Number.isFinite(value);
			case 'string':
				return Boolean(value);
			default:
				// fast way to check if value is not nil
				// eslint-disable-next-line eqeqeq
				return value != undefined;
		}
	};

	const handleFinish = () => {
		onFilter(getValues());
	};

	const handleReset = () => {
		form.resetFields();

		if (resetToInitial) {
			form.setFieldsValue({ ...initialValues });
		}
		if (onReset) onReset();
		else handleFinish();
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleSearch = useCallback(
		applyFilterImmediately
			? handleFinish
			: _debounce(handleFinish, applyFilterImmediately ? 0 : 500, { trailing: true }),
		[handleFinish]
	);

	const isResetActive = (): boolean => {
		// Reset button is active when at least one value is not nullable
		const values = form.getFieldsValue(true);
		if (resetToInitial && JSON.stringify(values) === JSON.stringify(initialValues)) {
			return false;
		}
		return Object.values(values).some(isValuePresent);
	};

	useEffect(() => {
		if (initialValues) {
			if (removeMissingFieldsOnInitialValuesChange) {
				// Cleanup form fields which are not present in 'initialValues' anymore
				const newFieldsSet = new Set(Object.keys(initialValues));
				const fieldsToResetObject = {};

				for (const presentField of Object.keys(form.getFieldsValue(true) || {})) {
					if (!newFieldsSet.has(presentField)) {
						Object.assign(fieldsToResetObject, { [presentField]: undefined });
					}
				}
				form.setFieldsValue(fieldsToResetObject);
			}

			form.setFieldsValue({ ...initialValues });
		}
	}, [form, initialValues, removeMissingFieldsOnInitialValuesChange]);

	const [isMoreVisible, setIsMoreVisible] = useState<boolean>(false);
	const [basicFilters, setBasicFilters] = useState<FilterBar2ListItem[]>([]);
	const [additionalFilters, setAdditionalFilters] = useState<FilterBar2ListItem[]>([]);

	useEffect(() => {
		setBasicFilters(list.filter(listItem => !listItem.additional || initialAdditional?.includes(listItem.name)));
		setAdditionalFilters(
			list.filter(listItem => listItem.additional && !initialAdditional?.includes(listItem.name))
		);
	}, [list, initialAdditional]);

	const onAdditionalChange = (list: FilterBar2ListItem[]) => {
		if (!onAdditional) {
			return;
		}

		onAdditional(list.filter(item => item.additional).map(item => item.name));
	};

	const onRemoveValue = (item: FilterBar2ListItem) => {
		form.resetFields([item.name]);
		if (onReset) onReset(item.name);
		else handleFinish();
	};

	const onAddFilter = (item: FilterBar2ListItem) => {
		setIsMoreVisible(false);

		const updatedBasicFilters = [...basicFilters, { ...item, temporaryOpened: true }];
		const updatedAdditionalFilters = additionalFilters.filter(filterItem => filterItem !== item);

		setBasicFilters(updatedBasicFilters);
		setAdditionalFilters(updatedAdditionalFilters);

		onAdditionalChange(updatedBasicFilters);
	};

	const onRemoveFilter = (item: FilterBar2ListItem) => {
		const updatedBasicFilters = basicFilters.filter(filterItem => filterItem !== item);
		const updatedAdditionalFilters = [...additionalFilters, item];

		setBasicFilters(updatedBasicFilters);
		setAdditionalFilters(updatedAdditionalFilters);

		onRemoveValue(item);
		onAdditionalChange(updatedBasicFilters);
	};

	const onCancelFilter = (item: FilterBar2ListItem) => {
		if (item.temporaryOpened && !getCount(item)) {
			onRemoveFilter(item);
		}
	};

	const onCloseTag = (item: FilterBar2ListItem) => {
		if (item.additional) {
			onRemoveFilter(item);
		} else {
			onRemoveValue(item);
		}
	};

	const onMoreVisibleChange = (value: boolean) => {
		setIsMoreVisible(value);
	};

	const getCount = (item: FilterBar2ListItem) => {
		const { type, name } = item;
		const value = form.getFieldValue(name);

		if (type === FilterBar2FilterType.CUSTOM && item.getCountOverride) {
			return item.getCountOverride(value);
		}

		if (type === FilterBar2FilterType.RADIO && item.customAllAsOption && value === undefined) {
			return null;
		}

		if (item.values) {
			if (
				_isNumber(value) ||
				([FilterBar2FilterType.SELECT, FilterBar2FilterType.RADIO].includes(type) && _isString(value))
			) {
				const filterBarValue = (item.values as FilterBar2Value[]).find(item => item.value === value);

				return filterBarValue.label || filterBarValue.value;
			}
		}

		if (_isEmpty(value)) {
			return null;
		}

		if (type === FilterBar2FilterType.DATE) {
			return value.format(dateFormat);
		}

		if (_isArray(value)) {
			if (type === FilterBar2FilterType.TIME_RANGE) {
				return `${value[0].format('HH:mm')} - ${value[1].format('HH:mm')}`;
			}

			if (type === FilterBar2FilterType.DATE_RANGE) {
				const timeFormat = item.customProps?.showTime?.format;
				const format = `${dateFormat}${
					item.customProps?.showTime ? (timeFormat ? ` ${timeFormat}` : ' HH:mm') : ''
				}`;
				return `${value[0].format(format)} - ${value[1].format(format)}`;
			}

			if (type === FilterBar2FilterType.DATE_RANGE_V2) {
				const timeFormat = item.customProps?.showTime?.format;
				const format = `${dateFormat}${
					item.customProps?.showTime ? (timeFormat ? ` ${timeFormat}` : ' HH:mm') : ''
				}`;

				if (isMomentRange(value)) {
					return `${value[0].format(format)} - ${value[1].format(format)}`;
				}

				if (isPairOfDateRangeKeywords(value)) {
					return item.customProps?.ranges?.[_first(value)]?.translatedLabel;
				}

				if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
					const [start, end] = [moment(value[0]), moment(value[1])];
					if (start.isValid() && end.isValid()) {
						return `${start.format(format)} - ${end.format(format)}`;
					}
				}

				return 'INVALID_DATE_RANGE_FORMAT';
			}

			if (value.length === 1) {
				const label = (item.values as FilterBar2Value[]).find(_item => _item.value === value[0])?.label;

				return label ?? value.length;
			}

			return value.length;
		}

		if (_isString(value)) {
			return value;
		}

		//for condition filters
		if (_has(value, 'condition')) {
			return getConditionFilterDisplayValue(value);
		}
	};

	const getConditionFilterDisplayValue = (value: FilterBar2ConditionValue) => {
		const { options: optionsText } = translations.conditionFilter;

		if (value.condition === FILTER_CONDITIONS.BETWEEN && value?.value) {
			const from = (value?.value as RangeValue)?.from ?? '';
			const to = (value?.value as RangeValue)?.to ?? '';

			return `${String(optionsText[value.condition]).toLowerCase()} ${from} - ${to}`;
		}

		return `${String(optionsText[value.condition]).toLowerCase()} ${value.value ?? ''}`;
	};

	return (
		<div className={classNames('filter-bar-2', className)} ref={popoverContainerRef}>
			<Form form={form} onFinish={handleFinish}>
				<Row justify="space-between" wrap={false}>
					<Col flex="auto" className="filter-bar-2-tags">
						<BringgIcon iconName={BringgFontIcons.FilterList} className="filter-list-icon" />
						{basicFilters.map(listItem => {
							if (listItem.type === FilterBar2FilterType.SEARCH) {
								return (
									<div className="filter-bar-2-search" key={listItem.name}>
										<Form.Item name={listItem.name} noStyle>
											<BringgInput
												type="search"
												// TODO: with new antd version we will be able to use our own icon for clear
												allowClear
												onChange={handleSearch}
												placeholder={listItem.placeholder}
												prefix={
													<BringgIcon
														className="search-icon"
														iconName={BringgFontIcons.Search}
													/>
												}
											/>
										</Form.Item>
									</div>
								);
							}

							if (listItem.disabled) {
								return (
									<FilterTag disabled={listItem.disabled}>
										{listItem.title}: <strong>{translations.all}</strong>
									</FilterTag>
								);
							}

							return (
								<FilterBase
									{...listItem}
									form={form}
									translations={translations}
									onCancel={() => onCancelFilter(listItem)}
									key={listItem.name}
									handleChangeDebounceMs={applyFilterImmediately ? 0 : undefined}
									popoverContainer={popoverContainerRef.current}
								>
									{({ isVisible }) => {
										const count = getCount(listItem);
										const formValue = form.getFieldValue(listItem.name);

										let allText = translations.all;

										if (listItem.customAllAsOption) {
											allText = listItem.customAllAsOption.label;
										}

										const isReversed = Boolean(!isVisible && !count);
										const isClosable = Boolean(
											!!listItem.closeable || listItem.closeable == undefined
										);

										return (
											<FilterTag
												reversed={isReversed}
												disabledButton={!count && !listItem.additional}
												disabled={listItem.disabled}
												closable={
													isClosable
														? isValuePresent(formValue) || listItem.additional
														: false
												}
												onClose={() => onCloseTag(listItem)}
											>
												<span data-test-id={`filter-tag-${listItem.name}`}>
													{listItem.title}:{' '}
													<strong>{count === null ? allText : count}</strong>
													<BringgIcon
														className="filter-chevron-icon"
														iconName={BringgFontIcons.Chevron}
													/>
												</span>
											</FilterTag>
										);
									}}
								</FilterBase>
							);
						})}
						{additionalFilters.length > 0 ? (
							<FilterMore
								list={additionalFilters}
								onAdd={onAddFilter}
								visible={isMoreVisible}
								onVisibleChange={onMoreVisibleChange}
							>
								{`+ ${translations.moreFilters}`}
							</FilterMore>
						) : null}
					</Col>

					<Col flex="none">
						{children ? (
							<Form.Item noStyle shouldUpdate>
								{() => children({ form })}
							</Form.Item>
						) : null}

						<Form.Item noStyle shouldUpdate>
							{() => (
								<Button
									type="link"
									className="filter-bar-2-button-blank"
									disabled={!isResetActive()}
									onClick={handleReset}
								>
									{translations.reset}
								</Button>
							)}
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</div>
	);
};

FilterBar2.TYPES = FilterBar2FilterType;

export default FilterBar2;
